import { useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import GenerateReportBody from "../../../components/Modals/GenerateReport";
import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import {
  BATCH_UPDATE_ARTWORK as batchUpdateArtworkMutation,
  DELETE_ARTWORK,
  REPROCESS_IMAGES,
} from "../../../graphql/mutation/Artwork";
import {
  FETCH_ARTWORK as fetchArtwork,
  FETCH_ARTWORKS_LIST as fetchArtworksList,
} from "../../../graphql/query/Artwork";
import { FETCH_LIST as fetchList } from "../../../graphql/query/List";
import useUser from "../../../hooks/useUser";
import routes from "../pages/Wizard/routes";
import AddOffersBody from "./Actions/AddOffersBody";
import AdhocReportBody from "./Actions/AdhocReportBody";
import BatchEditBody from "./Actions/BatchEditBody";
import CopyDescriptionBody from "./Actions/CopyDescriptionBody";
import exportModule from "./Actions/ExportModule";
import ArtworkSearch from "./ArtworkSearch";
import DuplicateArtwork from "./DuplicateArtwork";
import NewArtwork from "./NewArtwork";
import {
  artworkFilterStore,
  artworkNavigationStore,
} from "./useArtworkFilters";
import { clearSelectionState } from "../../../util/clearSelectionState";
import {
  getSearchAwareLabel,
  markModuleSearchSubmitted,
  useModuleSearchSubmitted,
} from "../../../util/usePersistedFilterFormik";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import { useSearchSynopsis } from "../../../hooks/useSearchSynopsis";
import {
  buildArtworkListUrl,
  buildArtworkNavigationTargetUrl,
  getArtworkListContext,
} from "./searchResultNavigation";

const ARTWORKS_NAVIGATION_QUERY = fetchArtworksList({});

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const isSearchSubmitted = useModuleSearchSubmitted(location.pathname);
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const search = artworkFilterStore.get();
  const pathnames = location?.pathname.split("/");
  const artworkId = pathnames?.length > 2 ? pathnames[2] : null; // get id from url
  const artworkIdNumber = artworkId ? Number(artworkId) : null;

  const { data: { artwork } = {} } = useQuery(
    fetchArtwork({
      artists: { contact: {} },
      artistDescription: {},
      editions: { artwork: {}, instances: {}, availableNumbers: {}, type: {} },
      fullDescription: {},
      pressDescription: {},
      images: { imgT: {}, imgX: {}, photoMaterial: {} },
      imagesSummary: { imgT: {}, imgX: {}, photoMaterial: {} },
      title: true,
    }),
    {
      skip: !artworkId,
      variables: { id: +artworkId },
    },
  );

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;
  const listContext = useMemo(
    () => getArtworkListContext(location.search),
    [location.search],
  );

  const { ids, select, disabled } = useMemo(() => {
    let select,
      ids = [];

    if (status === "imageSelectListWorking") {
      ids = Object.entries(formik.values.selectedImages?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.artwork?.id;
        },
      );
      select = formik?.values?.selectedImages?.select;
    } else {
      ids = Object.entries(formik.values.selected?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.id || value?.id;
        },
      );
      select = formik?.values?.selected?.select;
    }

    return {
      ids,
      select,
      disabled: ids?.length === 0,
    };
  }, [formik.values, status]);
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildArtworkNavigationTargetUrl,
    dataKey: "artworksV2",
    fetchQuery: ARTWORKS_NAVIGATION_QUERY,
    listContext,
    navigate,
    navigationStore: artworkNavigationStore,
    recordId: artworkIdNumber,
    status,
  });

  // Fetch list names when viewing saved lists (only if listId/imageListId is explicitly in URL and is a valid number)
  const { data: { list: savedList } = {} } = useQuery(fetchList(), {
    variables: { id: listId },
    skip: !listIdParam || !listId || isNaN(listId) || listId <= 0, // Only fetch if listId is explicitly in URL and valid
    errorPolicy: "ignore", // Ignore errors if list doesn't exist
  });

  const { data: { list: savedImageList } = {} } = useQuery(fetchList(), {
    variables: { id: imageListId },
    skip:
      !imageListIdParam ||
      !imageListId ||
      isNaN(imageListId) ||
      imageListId <= 0, // Only fetch if imageListId is explicitly in URL and valid
    errorPolicy: "ignore", // Ignore errors if list doesn't exist
  });

  const [
    deleteArtwork,
    {
      data: { deleteArtwork: { error: deleteArtworkError } = {} } = {},
      loading: deleteArtworkLoading,
      resetDelete,
    },
  ] = useMutation(DELETE_ARTWORK);
  const [
    batchUpdateArtwork,
    {
      data: { batchUpdateArtwork: { error: tbcArtworksError } = {} } = {},
      loading: tbcArtworksLoading,
    },
  ] = useMutation(batchUpdateArtworkMutation());
  const [reprocessImages] = useMutation(REPROCESS_IMAGES);

  const statusMap = {
    all: "All Artworks",
    searched: "Searched Artworks",
    selectListWorking:
      listIdParam && savedList?.name
        ? `Select List (${savedList.name})`
        : "Select List (Working)",
    imageSelectListWorking:
      imageListIdParam && savedImageList?.name
        ? `Image Select List (${savedImageList.name})`
        : "Image Select List (Working)",
  };
  const isShowingSearchResults = isSearchSubmitted && status === "all";
  const { synopsis: searchSynopsis } = useSearchSynopsis(
    artworkNavigationStore,
  );
  const label = getSearchAwareLabel(
    statusMap[status] || "All Artworks",
    isShowingSearchResults,
  );
  const rootLabel =
    isShowingSearchResults && searchSynopsis
      ? { label, title: `Artwork Search ${searchSynopsis}` }
      : label;
  // prepare steps for breadcrumbs
  const steps = [
    {
      label: rootLabel,
      onClick: () => navigate(buildArtworkListUrl({ listContext, status })),
    },
    ...(artwork
      ? [
          {
            label: artwork?.title,
            onClick: () =>
              navigate(
                buildArtworkNavigationTargetUrl({
                  listContext,
                  recordId: artwork?.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildArtworkNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: artwork?.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const searchModalProps = {
    body: ArtworkSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => {
      clearSelectionState(formik);
      markModuleSearchSubmitted(location.pathname);
    },
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: artworkId,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => clearSelectionState(formik),
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  // The artworks a brochure would be built from: the one being viewed on a
  // detail page, otherwise the explicit list selection.
  const brochureTargets = artworkId ? [+artworkId] : ids.filter(Boolean);

  const deleteModalProps = {
    title: deleteArtworkError ? "Error" : "Delete Artwork",
    scale: "sm",
    description: deleteArtworkError
      ? deleteArtworkError
      : "Are you sure you wish to delete this artwork? This cannot be undone.",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleDelete = () => {
        deleteArtwork({
          variables: {
            input: { id: artwork?.id },
          },
          update: (cache, { data }) => {
            const { deleteArtwork: { success } = {} } = data || {};
            if (success) {
              closeModal();
              cache.evict({
                id: cache.identify({
                  __typename: "Artwork",
                  id: artworkId,
                }),
              });
              navigate("/artworks");
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={deleteArtworkError ? "Close" : "Cancel"}
            action={deleteArtworkError ? "primary" : "default"}
            onClick={() => {
              resetDelete();
              closeModal?.();
            }}
          />
          {!deleteArtworkError && (
            <Button
              label={deleteArtworkLoading ? "Deleting" : "Delete"}
              disabled={deleteArtworkLoading}
              onClick={handleDelete}
            />
          )}
        </div>
      );
    },
  };

  const tbcModalProps = {
    title: tbcArtworksError ? "Error" : "TBC Artwork",
    scale: "sm",
    description: tbcArtworksError
      ? tbcArtworksError
      : "Are you sure you wish to add TBC to selected artworks?",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleClick = () => {
        batchUpdateArtwork({
          variables: {
            input: {
              ids: +artworkId ? [+artworkId] : ids,
              dimensions: "Dimensions to be confirmed",
              medium: "Medium to be confirmed",
              title: "[Title to be confirmed]",
            },
          },
          update: (cache, { data }) => {
            const { batchUpdateArtwork: { success } = {} } = data || {};
            if (success) {
              closeModal();
              formik?.resetForm?.();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={tbcArtworksError ? "Close" : "Cancel"}
            action={tbcArtworksError ? "primary" : "default"}
            onClick={() => {
              resetDelete();
              closeModal?.();
            }}
          />
          {!tbcArtworksError && (
            <Button
              label={tbcArtworksLoading ? "Processing" : "Confirm"}
              disabled={tbcArtworksLoading}
              onClick={handleClick}
            />
          )}
        </div>
      );
    },
  };

  const reprocessImagesModalProps = {
    title: "Reprocess Artwork Images",
    scale: "sm",
    closeOnBackdrop: true,
    description:
      "This function will reprocess any source artwork images and overwrite the transformed and resized images",
    renderBody: ({ closeModal }) => {
      const handleClick = () => {
        reprocessImages({
          variables: {
            id: +artwork?.id,
          },
          update: (cache, { data }) => {
            const { reprocessImages: { success, error } = {} } = data || {};
            if (success) {
              closeModal();
            } else {
              console.error(error);
            }
          },
        });
        closeModal?.();
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label="Cancel"
            action="default"
            onClick={() => {
              closeModal?.();
            }}
          />
          <Button label="Confirm" onClick={handleClick} />
        </div>
      );
    },
  };

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const [isBatchEditOpen, setIsBatchEditOpen] = useState(false);

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Artwork",
        modalProps: {
          body: NewArtwork,
          closeOnBackdrop: false,
          artwork,
          hideCloseButton: true,
          scale: "md",
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
        },
      },
      {
        label: "Copy Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Description",
          scale: "sm",
          description: "Do you wish to copy artwork descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        label: "Copy Archive Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Archive Description",
          type: "archiveDescription",
          scale: "sm",
          description: "Do you wish to copy artwork archive descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        label: "Copy Artist Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Artist Description",
          type: "artistDescription",
          scale: "sm",
          description: "Do you wish to copy artwork artist descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        label: "Copy Press Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Press Description",
          type: "pressDescription",
          scale: "sm",
          description: "Do you wish to copy artwork press descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        disabled,
        label: "Add to Offers Select List",
        modalProps: {
          body: AddOffersBody,
          ...defaultActionProps,
        },
      },
      {
        disabled,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !artwork?.id && disabled,
        label: "Export Artwork Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Artwork",
          ids: artwork?.id ? [artwork?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        // The brochure is built from instances, so the selected artworks are
        // sent as `artworkIds` and each collapses to its most recent instance.
        // Only explicit selections work: an artwork search can't be translated
        // into the instance search the wizard resolves its cohort with.
        disabled: brochureTargets.length === 0,
        label: (
          <>
            PDF Generator{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${
                brochureTargets.length === 0
                  ? "bg-gray-100 text-gray-400"
                  : "bg-green-100 text-green-700"
              }`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Instance",
          report: "brochure",
          ids: [],
          artworkIds: brochureTargets,
          // An artwork's instances all share its images and catalogue details;
          // the borrowed instance only supplies the reference and edition line,
          // so its sale price would be arbitrary.
          includePrices: false,
          listId: null,
          search: null,
          select: undefined,
          scale: "lg",
          onClose: () => {},
        },
      },
      {
        disabled: !ids.length && !artworkId,
        label: (
          <>
            Reporting{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${
                !ids.length && !artworkId
                  ? "bg-gray-100 text-gray-400"
                  : "bg-green-100 text-green-700"
              }`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Artwork",
          onClose: () => {},
        },
      },
      {
        disabled,
        label: "Batch Edit",
        modalProps: {
          body: BatchEditBody,
          isBatchEditOpen,
          onOpen: () => {
            setIsBatchEditOpen(true);
          },
          onClose: () => {
            setIsBatchEditOpen(false);
          },
          ...defaultActionProps,
        },
      },
      {
        disabled: !artwork?.id,
        label: "Reprocess Artwork Images",
        modalProps: reprocessImagesModalProps,
      },
      {
        disabled: !ids?.length && !artworkId,
        label: "TBC",
        modalProps: tbcModalProps,
      },
      ...(artworkId
        ? [
            {
              disabled: !hasPermission("DELETE_ARTWORK"),
              label: "Delete artwork",
              modalProps: deleteModalProps,
            },
            {
              label: "Duplicate Artwork",
              modalProps: {
                body: DuplicateArtwork,
                closeOnBackdrop: false,
                artwork,
                hideCloseButton: true,
                scale: "md",
              },
            },
          ]
        : []),
    ],
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <div id="form-save-status" />
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button
            label="Artwork Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} commandPalette />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
